// src/sectors/pilates/attendance/ui/AttendanceMarkModal/ParticipantAttendanceHistory.jsx
import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import Tooltip from '@mui/material/Tooltip';
import Icon from '@mui/material/Icon';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { S, META } from '../utils/attendanceConstants';
import { safeUpper, shortDateTime } from '../utils/attendanceUtils';

function trISO(iso) {
  if (!iso) return '—';
  const s = String(iso);
  const y = s.slice(0, 4);
  const m = s.slice(5, 7);
  const d = s.slice(8, 10);
  if (!y || !m || !d) return s;
  return `${d}.${m}.${y}`;
}

function hmFromMin(min) {
  if (min == null) return '';
  const v = Number(min);
  if (!Number.isFinite(v)) return '';
  const hh = String(Math.floor(v / 60)).padStart(2, '0');
  const mm = String(v % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

function trStatusLabel(raw) {
  const st = safeUpper(raw);
  if (st === 'ATTENDED' || st === 'PRESENT') return 'Geldi';
  if (st === 'ABSENT' || st === 'NO_SHOW') return 'Gelmedi';
  if (st === 'CANCELLED') return 'İptal';
  if (st === 'BURNED' || st === 'BURN') return 'Yandı';
  return 'Bekliyor';
}

function isMakeUpSession(x) {
  const t = safeUpper(x?.sessionType);
  if (!t) return false;
  return (
    t.includes('MAKE') || t.includes('TELAFI') || t.includes('MAKEUP') || t.includes('MAKE_UP')
  );
}

function statusMeta(stRaw) {
  const st = safeUpper(stRaw);
  return META[st] || META[S.PENDING];
}

function statusColor(theme, stRaw) {
  const m = statusMeta(stRaw);
  return theme.palette[m.colorKey]?.main || theme.palette.info.main;
}

/** ===== Header (Son 5 form pill) ===== */
function formKind(raw) {
  const st = safeUpper(raw);
  if (st === 'ATTENDED' || st === 'PRESENT') return 'W';
  if (st === 'ABSENT' || st === 'NO_SHOW' || st === 'BURNED' || st === 'BURN') return 'L';
  if (st === 'CANCELLED') return 'C';
  return 'P';
}

function FormPill({ kind, title }) {
  const theme = useMuiTheme();
  const cfg = (() => {
    if (kind === 'W') return { icon: 'check', colorKey: 'success' };
    if (kind === 'L') return { icon: 'close', colorKey: 'error' };
    if (kind === 'C') return { icon: 'remove', colorKey: 'warning' };
    return { icon: 'more_horiz', colorKey: 'info' };
  })();
  const c = theme.palette[cfg.colorKey]?.main || theme.palette.info.main;

  return (
    <Tooltip title={title || ''} arrow placement="top">
      <MDBox
        sx={{
          width: 22,
          height: 22,
          borderRadius: '999px',
          display: 'grid',
          placeItems: 'center',
          bgcolor: alpha(c, theme.palette.mode === 'dark' ? 0.18 : 0.14),
          border: `1px solid ${alpha(c, 0.28)}`,
        }}
      >
        <Icon sx={{ fontSize: 16, color: alpha(c, 0.95) }}>{cfg.icon}</Icon>
      </MDBox>
    </Tooltip>
  );
}

FormPill.propTypes = {
  kind: PropTypes.oneOf(['W', 'L', 'C', 'P']).isRequired,
  title: PropTypes.string,
};
FormPill.defaultProps = { title: '' };

function StatusPill({ label, color }) {
  return (
    <MDBox
      sx={{
        height: 22,
        px: 0.9,
        borderRadius: '999px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        bgcolor: alpha(color, 0.12),
        border: `1px solid ${alpha(color, 0.26)}`,
      }}
    >
      <MDBox
        sx={{
          width: 6,
          height: 6,
          borderRadius: '999px',
          bgcolor: alpha(color, 0.95),
          boxShadow: `0 0 0 3px ${alpha(color, 0.12)}`,
        }}
      />
      <MDTypography variant="caption" sx={{ fontWeight: 900, color: alpha(color, 0.95) }}>
        {label}
      </MDTypography>
    </MDBox>
  );
}

StatusPill.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

function TagPill({ label, color }) {
  return (
    <MDBox
      sx={{
        height: 22,
        px: 0.9,
        borderRadius: '999px',
        display: 'inline-flex',
        alignItems: 'center',
        bgcolor: alpha(color, 0.1),
        border: `1px solid ${alpha(color, 0.2)}`,
      }}
    >
      <MDTypography variant="caption" sx={{ fontWeight: 800, color: alpha(color, 0.9) }}>
        {label}
      </MDTypography>
    </MDBox>
  );
}
TagPill.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

export function ParticipantAttendanceHistory({ p, variant }) {
  const theme = useMuiTheme();
  const [expanded, setExpanded] = useState(null);

  const last5Form = useMemo(() => {
    const xs =
      (Array.isArray(p?.last5) && p.last5) ||
      (Array.isArray(p?.lastAttendances) && p.lastAttendances) ||
      (Array.isArray(p?.attendanceLast5) && p.attendanceLast5) ||
      [];
    return xs
      .filter(Boolean)
      .slice(0, 5)
      .map((x) => {
        const st = x?.status || x?.attendanceStatus || x;
        const day = x?.dayISO ? trISO(x.dayISO) : '';
        const k = formKind(st);
        const title =
          k === 'W'
            ? `Geldi ${day ? `• ${day}` : ''}`
            : k === 'L'
            ? `Gelmedi ${day ? `• ${day}` : ''}`
            : k === 'C'
            ? `İptal ${day ? `• ${day}` : ''}`
            : `Bekliyor ${day ? `• ${day}` : ''}`;
        return { k, title };
      });
  }, [p]);

  const rows = useMemo(() => {
    const xs = Array.isArray(p?.packageAttendances) ? p.packageAttendances : [];

    // ✅ EN YENİ -> EN ESKİ
    return xs
      .filter(Boolean)
      .slice()
      .sort((a, b) => {
        const da = String(a?.dayISO || '');
        const db = String(b?.dayISO || '');
        if (da !== db) return db.localeCompare(da); // DESC
        return Number(b?.startMin ?? 0) - Number(a?.startMin ?? 0); // DESC
      });
  }, [p]);

  const onAccChange = useCallback(
    (panelId) => (_, isExpanded) => {
      setExpanded(isExpanded ? panelId : null);
    },
    []
  );

  if (variant === 'header') {
    if (!last5Form.length) return null;
    return (
      <MDBox sx={{ display: 'flex', gap: 0.35, flexShrink: 0 }}>
        {last5Form.map((it, i) => (
          <FormPill key={i} kind={it.k} title={it.title} />
        ))}
      </MDBox>
    );
  }

  return (
    <MDBox sx={{ display: 'grid', gap: 0.55 }}>
      <MDTypography variant="caption" sx={{ opacity: 0.9, fontWeight: 900 }}>
        Paket Yoklamaları
      </MDTypography>

      {rows.length ? (
        <MDBox sx={{ display: 'grid', gap: 0.45 }}>
          {rows.map((x, idx) => {
            const panelId = String(x?.sessionId ?? `${x?.dayISO}-${x?.startMin}-${idx}`);

            const day = trISO(x?.dayISO);
            const hm = hmFromMin(x?.startMin);

            const stLabel = trStatusLabel(x?.attendanceStatus);
            const stColor = statusColor(theme, x?.attendanceStatus);

            const makeUp = isMakeUpSession(x);
            const makeUpColor = makeUp ? theme.palette.warning.main : theme.palette.info.main;

            const markedBy = (x?.markedByName || '').trim() || '—';
            const markedAt = x?.markedAt ? shortDateTime(x.markedAt) : null;

            const trainerName = (x?.trainerName || '').trim() || '—';

            const note = (x?.note || '').trim();
            const hasNote = Boolean(note);

            return (
              <Accordion
                key={panelId}
                expanded={expanded === panelId}
                onChange={onAccChange(panelId)}
                disableGutters
                elevation={0}
                sx={{
                  borderRadius: '16px',
                  border: `1px solid ${alpha(stColor, 0.18)}`,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? `linear-gradient(180deg, ${alpha(stColor, 0.08)}, ${alpha('#fff', 0.02)})`
                      : `linear-gradient(180deg, ${alpha(stColor, 0.06)}, ${alpha(
                          '#0b1220',
                          0.01
                        )})`,
                  overflow: 'hidden',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<Icon sx={{ fontSize: 18, opacity: 0.85 }}>expand_more</Icon>}
                  sx={{
                    minHeight: 0,
                    '& .MuiAccordionSummary-content': { my: 0 },
                    px: 1.0,
                    py: 0.85,
                    transition: 'background .12s ease',
                    '&:hover': {
                      bgcolor: alpha(stColor, theme.palette.mode === 'dark' ? 0.06 : 0.05),
                    },
                  }}
                >
                  {/* KAPALI LISTE SATIRI */}
                  <MDBox sx={{ minWidth: 0, display: 'grid', gap: 0.4, width: '100%' }}>
                    {/* üst satır */}
                    <MDBox
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0 }}>
                        <MDTypography
                          variant="caption"
                          sx={{ fontWeight: 900, opacity: 0.9 }}
                          noWrap
                        >
                          {day}
                          {hm ? ` ${hm}` : ''}
                        </MDTypography>

                        {/* durum renkli */}
                        <StatusPill label={stLabel} color={stColor} />

                        {/* telafi/normal */}
                        <TagPill label={makeUp ? 'Telafi' : 'Normal'} color={makeUpColor} />
                      </MDBox>

                      {/* not ünlem */}
                      {hasNote ? (
                        <Tooltip title="Not var" arrow>
                          <MDBox
                            sx={{
                              width: 22,
                              height: 22,
                              borderRadius: '999px',
                              display: 'grid',
                              placeItems: 'center',
                              bgcolor: alpha(theme.palette.warning.main, 0.14),
                              border: `1px solid ${alpha(theme.palette.warning.main, 0.28)}`,
                              flex: '0 0 auto',
                            }}
                          >
                            <Icon
                              sx={{ fontSize: 16, color: alpha(theme.palette.warning.main, 0.95) }}
                            >
                              priority_high
                            </Icon>
                          </MDBox>
                        </Tooltip>
                      ) : null}
                    </MDBox>

                    {/* alt satır: yoklamayı alan kişi (kapalıyken) */}
                    <MDTypography variant="caption" sx={{ opacity: 0.7 }} noWrap>
                      Yoklamayı alan: <b>{markedBy}</b>
                      {markedAt ? ` • ${markedAt}` : ''}
                    </MDTypography>
                  </MDBox>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 1.0, pb: 1.0, pt: 0.2 }}>
                  {/* AKORDİYON İÇİ */}
                  <MDBox sx={{ display: 'grid', gap: 0.55 }}>
                    <MDTypography variant="caption" sx={{ opacity: 0.88 }}>
                      Derse giren eğitmen: <b>{trainerName}</b>
                    </MDTypography>

                    {hasNote ? (
                      <MDBox
                        sx={{
                          p: 0.85,
                          borderRadius: '14px',
                          border: `1px dashed ${alpha(theme.palette.warning.main, 0.42)}`,
                          bgcolor: alpha(
                            theme.palette.warning.main,
                            theme.palette.mode === 'dark' ? 0.07 : 0.06
                          ),
                        }}
                      >
                        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.35 }}>
                          <Icon
                            sx={{ fontSize: 16, color: alpha(theme.palette.warning.main, 0.95) }}
                          >
                            sticky_note_2
                          </Icon>
                          <MDTypography variant="caption" sx={{ fontWeight: 900, opacity: 0.9 }}>
                            Not
                          </MDTypography>
                        </MDBox>

                        <MDTypography variant="caption" sx={{ opacity: 0.85 }}>
                          {note}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      <MDTypography variant="caption" sx={{ opacity: 0.6 }}>
                        Not yok.
                      </MDTypography>
                    )}
                  </MDBox>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </MDBox>
      ) : (
        <MDTypography variant="caption" sx={{ opacity: 0.65 }}>
          Paket yoklaması bulunamadı.
        </MDTypography>
      )}
    </MDBox>
  );
}

ParticipantAttendanceHistory.propTypes = {
  p: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(['header', 'detail']),
};

ParticipantAttendanceHistory.defaultProps = {
  variant: 'detail',
};
